import { useEffect, useState } from 'react';
import axios from 'axios';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { formatDate } from '../../lib/utils';
import {
    UserPlus, Users, Mail, Shield, ShieldOff, Trash2, Loader2, AlertTriangle, Crown, User, UserCog
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
    { value: 'member', label: 'Member', desc: 'Can view dashboard and codes' },
    { value: 'manager', label: 'Manager', desc: 'Can manage codes and send to clients' },
    { value: 'admin', label: 'Admin', desc: 'Full access to all operator features' }
];

const roleIcon = (role) => {
    if (role === 'admin') return Crown;
    if (role === 'manager') return UserCog;
    return User;
};

const roleColor = (role) => {
    if (role === 'admin') return 'bg-gold/10 text-gold';
    if (role === 'manager') return 'bg-blue-500/10 text-blue-400';
    return 'bg-white/5 text-muted-foreground';
};

export default function OperatorTeam() {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    const [form, setForm] = useState({ email: '', name: '', role_label: 'member' });

    useEffect(() => { fetchMembers(); }, []);

    const fetchMembers = async () => {
        try {
            const res = await axios.get(`${API}/api/operator/team`, { withCredentials: true });
            setMembers(res.data);
        } catch (e) {
            toast.error('Failed to load team');
        } finally {
            setLoading(false);
        }
    };

    const handleInvite = async (e) => {
        e.preventDefault();
        if (!form.email || !form.name) return toast.error('Name and email are required');
        setSaving(true);
        try {
            await axios.post(`${API}/api/operator/team/invite`, form, { withCredentials: true });
            toast.success(`Invitation sent to ${form.email}`);
            setInviteOpen(false);
            setForm({ email: '', name: '', role_label: 'member' });
            fetchMembers();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to invite');
        } finally {
            setSaving(false);
        }
    };

    const handleRemove = async () => {
        if (!selectedMember) return;
        setDeleting(true);
        try {
            await axios.delete(`${API}/api/operator/team/${selectedMember.id}`, { withCredentials: true });
            toast.success('Team member removed');
            setDeleteOpen(false);
            setSelectedMember(null);
            fetchMembers();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to remove');
        } finally {
            setDeleting(false);
        }
    };

    const openRemove = (m) => { setSelectedMember(m); setDeleteOpen(true); };

    if (loading) {
        return (
            <DashboardLayout type="operator">
                <div className="flex items-center justify-center h-64">
                    <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout type="operator">
            <div className="space-y-8" data-testid="operator-team">
                {/* Header */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="font-outfit text-2xl lg:text-3xl font-bold text-white">Team Members</h1>
                        <p className="text-muted-foreground mt-1">Manage who has access to your operator dashboard</p>
                    </div>
                    <Button
                        onClick={() => setInviteOpen(true)}
                        className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2"
                        data-testid="invite-member-btn"
                    >
                        <UserPlus className="w-4 h-4" /> Invite Member
                    </Button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4">
                    {[
                        { label: 'Total Members', value: members.length, icon: Users, color: 'text-white' },
                        { label: 'Managers', value: members.filter(m => m.role_label === 'manager').length, icon: UserCog, color: 'text-blue-400' },
                        { label: 'Admins', value: members.filter(m => m.role_label === 'admin').length, icon: Crown, color: 'text-gold' }
                    ].map((s, i) => (
                        <Card key={i} className="bg-[#121620] border-white/10">
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-xs text-muted-foreground">{s.label}</p>
                                        <p className={`text-2xl font-bold font-outfit ${s.color}`}>{s.value}</p>
                                    </div>
                                    <s.icon className={`w-6 h-6 ${s.color} opacity-30`} />
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                {/* Members Table */}
                <Card className="bg-[#121620] border-white/10">
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow className="border-white/10 hover:bg-transparent">
                                    <TableHead className="text-muted-foreground">Member</TableHead>
                                    <TableHead className="text-muted-foreground">Email</TableHead>
                                    <TableHead className="text-muted-foreground">Role</TableHead>
                                    <TableHead className="text-muted-foreground">Status</TableHead>
                                    <TableHead className="text-muted-foreground">Joined</TableHead>
                                    <TableHead className="text-muted-foreground text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {members.map((m) => {
                                    const RoleIcon = roleIcon(m.role_label);
                                    return (
                                        <TableRow key={m.id} className="border-white/10 hover:bg-white/5">
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center">
                                                        <RoleIcon className="w-5 h-5 text-gold" />
                                                    </div>
                                                    <p className="text-white font-medium">{m.name || 'Unnamed'}</p>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1 text-muted-foreground text-sm">
                                                    <Mail className="w-3 h-3" />
                                                    <span>{m.email}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <span className={`px-2 py-1 rounded-full text-xs capitalize ${roleColor(m.role_label)}`}>
                                                    {m.role_label}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                {m.is_active !== false ? (
                                                    <span className="flex items-center gap-1 text-emerald-400 text-xs">
                                                        <Shield className="w-3 h-3" /> Active
                                                    </span>
                                                ) : (
                                                    <span className="flex items-center gap-1 text-red-400 text-xs">
                                                        <ShieldOff className="w-3 h-3" /> Inactive
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <span className="text-sm text-muted-foreground">{formatDate(m.invited_at)}</span>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => openRemove(m)}
                                                    className="text-muted-foreground hover:text-red-400"
                                                    data-testid={`remove-member-${m.id}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {members.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                                            <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
                                            <p>No team members yet. Invite someone to collaborate.</p>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>

                {/* Invite Dialog */}
                <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
                    <DialogContent className="bg-[#121620] border-white/10 max-w-md">
                        <DialogHeader>
                            <DialogTitle className="text-white font-outfit flex items-center gap-2">
                                <UserPlus className="w-5 h-5 text-gold" /> Invite Team Member
                            </DialogTitle>
                            <DialogDescription className="text-muted-foreground text-sm">
                                They'll receive an email invite to join your operator dashboard.
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleInvite} className="space-y-4 mt-2">
                            <div className="space-y-2">
                                <Label>Name</Label>
                                <Input
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    placeholder="Team member name"
                                    className="bg-[#0A0D14] border-white/10"
                                    data-testid="invite-name"
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Email</Label>
                                <Input
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    placeholder="team@company.com"
                                    className="bg-[#0A0D14] border-white/10"
                                    data-testid="invite-email"
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Role</Label>
                                <Select value={form.role_label} onValueChange={(v) => setForm({ ...form, role_label: v })}>
                                    <SelectTrigger className="bg-[#0A0D14] border-white/10" data-testid="invite-role">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#121620] border-white/10">
                                        {ROLES.map(r => (
                                            <SelectItem key={r.value} value={r.value}>
                                                <div>
                                                    <span>{r.label}</span>
                                                    <span className="text-muted-foreground ml-2 text-xs">— {r.desc}</span>
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex justify-end gap-3 pt-2">
                                <Button type="button" variant="outline" onClick={() => setInviteOpen(false)} className="border-white/10">Cancel</Button>
                                <Button type="submit" disabled={saving} className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2" data-testid="send-invite-btn">
                                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                                    Send Invite
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>

                {/* Remove Confirmation */}
                <Dialog open={deleteOpen} onOpenChange={(open) => { setDeleteOpen(open); if (!open) setSelectedMember(null); }}>
                    <DialogContent className="bg-[#121620] border-white/10 max-w-sm">
                        <DialogHeader>
                            <DialogTitle className="text-white font-outfit flex items-center gap-2">
                                <AlertTriangle className="w-5 h-5 text-red-400" /> Remove Member
                            </DialogTitle>
                            <DialogDescription className="text-muted-foreground text-sm">This action cannot be undone.</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 mt-2">
                            <p className="text-muted-foreground">
                                Remove <span className="text-white font-medium">{selectedMember?.name || selectedMember?.email}</span> from your team?
                                They will lose access to the operator dashboard.
                            </p>
                            <div className="flex justify-end gap-3">
                                <Button variant="outline" onClick={() => setDeleteOpen(false)} className="border-white/10">Cancel</Button>
                                <Button onClick={handleRemove} disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white gap-2" data-testid="confirm-remove-btn">
                                    {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                    Remove
                                </Button>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        </DashboardLayout>
    );
}
